import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import { toast } from 'vue3-toastify';

import registerApi from '~/services/register-api/register-api';
import {
  invalidatePartnerDetailsQueries,
  invalidatePartnerUsersQueries,
} from '~/store/queryClient';
import { parseAssociateGeneralError } from '~/utils/apiFieldErrors';

const MAX_RETRIES = 3;
const NO_RETRY_STATUS = new Set([401, 403, 404, 409, 422]);

export type DeleteAssociateVariables = {
  companyId: number;
  data: Parameters<typeof registerApi.associates.delete>[1];
};

export type UpdateAssociateVariables = {
  companyId: number;
  data: Parameters<typeof registerApi.associates.update>[1];
};

/* eslint-disable perfectionist/sort-modules -- helpers antes dos hooks */
function mutationRetry(failureCount: number, error: unknown): boolean {
  if (isAxiosError(error) && error.response && NO_RETRY_STATUS.has(error.response.status)) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}

export function useDeleteAssociate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: DeleteAssociateVariables) => (
      registerApi.associates.delete(variables.companyId, variables.data)
    ),
    onError: (error: unknown) => {
      toast.error(parseAssociateGeneralError(error, 'Erro ao inativar sócio'));
    },
    onSuccess: async () => {
      await invalidatePartnerDetailsQueries(queryClient);
      toast.success('Sócio inativado com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}

export function useUpdateAssociate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: UpdateAssociateVariables) => (
      registerApi.associates.update(variables.companyId, variables.data)
    ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidatePartnerDetailsQueries(queryClient),
        invalidatePartnerUsersQueries(queryClient, variables.companyId),
      ]);
      toast.success('Sócio atualizado com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}
/* eslint-enable perfectionist/sort-modules */
